import { createObservation, getReports, renameReport, ReportResponse } from '@api/observationApi';
import useNotificationStore from '@gui/store/notification';
import usePersistentStore, { useGlobalBucket } from '@gui/store/persistent/persistentStore.js';
import { Button, CircularProgress, Divider, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ScreenshotExporter, ScreenshotResolution } from '../../../../Utils/ScreenshotExporter';
import { NotificationType, UUID } from '@visualizer/types';
import { getDisabledInEnvMessage, useEnvStore } from 'store/EnvStore';
import useViewerStore from 'store/viewerStore';
import { useCapabilities } from '../../../../demo/capabilities';

type ObservationManagerProps = {
    gridId: string;
    canUseApi: boolean;
    showContent: boolean;
};

type ReportOption = {
    id: string;
    name: string;
};

const CREATE_NEW_REPORT_ID = 'create-new-report-option';

const ObservationManager: React.FC<ObservationManagerProps> = ({ gridId, canUseApi, showContent }) => {
    const showNotification = useNotificationStore((s) => s.showNotification);
    const { canSaveObservation } = useCapabilities();
    const env = useEnvStore((s) => s.env);
    const viewer = useViewerStore((state) => state.viewer);
    if (!viewer) return null;

    const currentCamera = useGlobalBucket((s) => s.ambient.currentCamera);

    const [observationTitle, setObservationTitle] = useState('');
    const [observationDescription, setObservationDescription] = useState('');
    const [reports, setReports] = useState<ReportOption[]>([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const [reportsFetched, setReportsFetched] = useState(false);
    const [selectedReportId, setSelectedReportId] = useState(CREATE_NEW_REPORT_ID);
    const [savingObservation, setSavingObservation] = useState(false);
    const [newReportName, setNewReportName] = useState('');
    const [renamingReportId, setRenamingReportId] = useState<string | null>(null);
    const [renamingReportName, setRenamingReportName] = useState('');
    const [renamingReportPending, setRenamingReportPending] = useState(false);

    const fetchReports = useCallback(async () => {
        setReportsLoading(true);
        try {
            const response = await getReports();
            const list = (Array.isArray(response) ? response : []) as ReportResponse[];
            const mapped = list.map((report) => ({ id: report.id, name: report.name }));
            setReports(mapped);
            setSelectedReportId((prev) => {
                if (prev && mapped.some((report) => report.id === prev)) return prev;
                if (mapped[0]?.id) return mapped[0].id;
                return CREATE_NEW_REPORT_ID;
            });
        } catch (error) {
            console.error(error);
            showNotification('Error contacting server', NotificationType.Error);
        } finally {
            setReportsLoading(false);
            setReportsFetched(true);
        }
    }, [showNotification]);

    useEffect(() => {
        if (showContent && !reportsFetched && canUseApi) fetchReports();
    }, [showContent, reportsFetched, canUseApi, fetchReports]);

    const captureScreenshot = useCallback(async (): Promise<Blob | null> => {
        try {
            const exporter = new ScreenshotExporter();
            const data = await exporter.getScreenshot(ScreenshotResolution.HD, true, 'png');
            if (!data || data.length === 0) return null;
            const buffer = data.buffer as ArrayBuffer;
            return new Blob([buffer], { type: 'image/png' });
        } catch (error) {
            console.warn('Unable to capture screenshot for observation.', error);
            return null;
        }
    }, []);

    const selectedReportName = useMemo(() => {
        const report = reports.find((item) => item.id === selectedReportId);
        return report?.name ?? '';
    }, [reports, selectedReportId]);
    const isCreatingNewReport = selectedReportId === CREATE_NEW_REPORT_ID;

    const handleCommitRename = useCallback(async () => {
        if (!renamingReportId) return;
        const trimmedName = renamingReportName.trim();
        if (!trimmedName) return;
        const currentReport = reports.find((item) => item.id === renamingReportId);
        if (!currentReport || currentReport.name === trimmedName) {
            setRenamingReportId(null);
            return;
        }
        setRenamingReportPending(true);
        try {
            await renameReport(renamingReportId, trimmedName);
            setReports((prev) => prev.map((report) => (report.id === renamingReportId ? { ...report, name: trimmedName } : report)));
            setRenamingReportId(null);
        } catch (error) {
            console.error(error);
            showNotification('Error contacting server', NotificationType.Error);
        } finally {
            setRenamingReportPending(false);
        }
    }, [renamingReportId, renamingReportName, reports, showNotification]);

    const handleSaveObservation = useCallback(async () => {
        const trimmedTitle = observationTitle.trim();
        const selectedName = isCreatingNewReport ? newReportName.trim() : selectedReportName.trim();
        if (!canUseApi || !gridId || !trimmedTitle || !selectedName) return;
        setSavingObservation(true);
        viewer.cameraHelper.refreshStore(currentCamera.type);
        requestAnimationFrame(() => {
            requestAnimationFrame(async () => {
                try {
                    const storageName = usePersistentStore.persist.getOptions().name!;
                    const serializedState = localStorage.getItem(storageName);
                    if (!serializedState) throw new Error('Cannot access local storage content');

                    const stateBlob = new Blob([serializedState], { type: 'application/json' });
                    const screenshotBlob = await captureScreenshot();

                    if (!screenshotBlob) {
                        throw new Error('Unable to capture screenshot');
                    }

                    const observationPayload = {
                        title: trimmedTitle,
                        description: observationDescription,
                        grid_id: gridId as UUID,
                        reportId: isCreatingNewReport ? null : (selectedReportId as UUID),
                        reportName: isCreatingNewReport ? selectedName : null,
                    };

                    await createObservation(observationPayload, screenshotBlob, stateBlob);
                    fetchReports();
                    setObservationTitle('');
                    setObservationDescription('');
                    setNewReportName('');
                    showNotification('Observation saved successfully.', NotificationType.Success);
                } catch (error) {
                    console.error(error);
                    showNotification('Error contacting server', NotificationType.Error);
                } finally {
                    setSavingObservation(false);
                }
            });
        });
    }, [
        captureScreenshot,
        canUseApi,
        gridId,
        newReportName,
        observationDescription,
        observationTitle,
        selectedReportName,
        isCreatingNewReport,
        fetchReports,
        showNotification,
    ]);

    const preventKeyPropagation = useCallback((event: React.KeyboardEvent) => {
        event.stopPropagation();
    }, []);

    const observationSaveDisabled =
        !canSaveObservation ||
        !canUseApi ||
        !gridId ||
        !observationTitle.trim() ||
        reportsLoading ||
        savingObservation ||
        (isCreatingNewReport ? !newReportName.trim() : !selectedReportName.trim());

    if (!showContent) return null;

    return (
        <>
            <Stack spacing={1.5} mt={1.5}>
                {renamingReportId === selectedReportId ? (
                    <TextField
                        label="Rename report"
                        value={renamingReportName}
                        onChange={(event) => setRenamingReportName(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                handleCommitRename();
                            }
                            if (event.key === 'Escape') {
                                event.preventDefault();
                                setRenamingReportId(null);
                            }
                        }}
                        onBlur={() => setRenamingReportId(null)}
                        fullWidth
                        disabled={renamingReportPending}
                        autoFocus
                    />
                ) : (
                    <TextField
                        select
                        label="Select Report"
                        value={selectedReportId}
                        size="small"
                        onChange={(event) => {
                            const value = event.target.value as string;
                            setSelectedReportId(value);
                            setRenamingReportId(null);
                        }}
                        onDoubleClick={() => {
                            if (!selectedReportId || selectedReportId === CREATE_NEW_REPORT_ID) return;
                            const report = reports.find((item) => item.id === selectedReportId);
                            if (!report) return;
                            setRenamingReportId(selectedReportId);
                            setRenamingReportName(report.name);
                        }}
                        fullWidth
                        disabled={reportsLoading || renamingReportPending}
                        SelectProps={{
                            renderValue: (value) => {
                                if (value === CREATE_NEW_REPORT_ID) return 'Create New Report';
                                const report = reports.find((item) => item.id === value);
                                return report?.name ?? '';
                            },
                        }}
                    >
                        {reports.map((report) => (
                            <MenuItem key={report.id} value={report.id}>
                                {report.name}
                            </MenuItem>
                        ))}
                        <Divider sx={{ my: 0.5 }} />
                        <MenuItem value={CREATE_NEW_REPORT_ID}>Create New Report</MenuItem>
                    </TextField>
                )}
                {isCreatingNewReport && (
                    <TextField
                        label="New report name*"
                        value={newReportName}
                        size="small"
                        onChange={(event) => setNewReportName(event.target.value)}
                        onKeyDown={preventKeyPropagation}
                        slotProps={{
                            htmlInput: { maxLength: 256 },
                        }}
                        fullWidth
                        autoFocus
                    />
                )}
                <Divider></Divider>
                <TextField
                    label="Title"
                    size="small"
                    onKeyDown={preventKeyPropagation}
                    value={observationTitle}
                    onChange={(event) => setObservationTitle(event.target.value)}
                    required
                    slotProps={{
                        htmlInput: { maxLength: 256 },
                    }}
                    fullWidth
                />
                <TextField
                    label="Description"
                    onKeyDown={preventKeyPropagation}
                    value={observationDescription}
                    onChange={(event) => setObservationDescription(event.target.value)}
                    multiline
                    size="small"
                    minRows={2}
                    fullWidth
                />
            </Stack>
            <Tooltip title={!canSaveObservation ? getDisabledInEnvMessage(env) : ''}>
                <span>
                    <Button
                        style={{ marginTop: '10px' }}
                        onClick={handleSaveObservation}
                        disabled={observationSaveDisabled}
                        variant="contained"
                    >
                        Save
                        {savingObservation && <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />}
                    </Button>
                </span>
            </Tooltip>
        </>
    );
};

export default ObservationManager;
